import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Send, Loader2, Copy, Check, Mail, MessageSquare, Reply, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const input = "w-full rounded-sm bg-navy border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const label = "block text-xs uppercase tracking-wider text-slate-400 mb-1.5";

export function QuoteReplyDialog({ quote, onReplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({ amount: quote.agreed_price ? String(quote.agreed_price) : "", payment_method: "online", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm({ amount: "", payment_method: "online", message: "" }); setResult(null); };
  const copy = (t) => { navigator.clipboard.writeText(t); setCopied(t); toast.success("Copied!"); setTimeout(() => setCopied(""), 1500); };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid quoted price."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/quote-requests/${quote.id}/reply`, {
        amount: amt,
        payment_method: form.payment_method,
        message: form.message.trim() || undefined,
      });
      setResult(data);
      if (data.email_sent) toast.success(`Quote emailed to ${quote.email}`);
      else toast.success("Quote created. Share the link below.");
      onReplied?.(quote.id, data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send quote.");
    } finally {
      setLoading(false);
    }
  };

  const payUrl = result?.pay_url || "";
  const msg = result
    ? `Hi${quote.name ? " " + quote.name : ""}, here is your quote from Bridge Taxi — $${result.amount.toFixed(2)}. Pay/view here: ${payUrl}`
    : "";
  const mailto = `mailto:${quote.email || ""}?subject=${encodeURIComponent("Your quote — Bridge Taxi")}&body=${encodeURIComponent(msg)}`;
  const sms = `sms:${quote.phone || ""}?&body=${encodeURIComponent(msg)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid={`reply-quote-${quote.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold hover:text-gold-light">
          <Reply className="h-3 w-3" /> {quote.status === "quoted" ? "Re-send price" : "Send price"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-navy-light border border-gold/30 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading">Reply with a price</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Send {quote.name || "the customer"} an agreed price and a secure pay link in one click.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-sm bg-navy border border-gold/15 px-4 py-3 text-xs text-slate-300 space-y-0.5">
          <div><span className="text-slate-500">Customer:</span> {quote.name || "—"} · {quote.phone || "no phone"} · {quote.email || "no email"}</div>
          <div><span className="text-slate-500">Needs:</span> {quote.service_type || "Unspecified"}</div>
          <div><span className="text-slate-500">Route:</span> {quote.pickup_location || "—"}{quote.dropoff_location ? ` → ${quote.dropoff_location}` : ""}</div>
          {quote.message ? <div><span className="text-slate-500">Message:</span> {quote.message}</div> : null}
        </div>

        {!result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Quoted price (CAD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">$</span>
                  <input data-testid="qr-amount" type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} placeholder="0.00" className={`${input} pl-7`} />
                </div>
              </div>
              <div>
                <label className={label}>Payment method</label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="qr-method" className="bg-navy border-gold/20 text-white h-[46px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Card (+HST 15%)</SelectItem>
                    <SelectItem value="e_transfer">Interac e-Transfer</SelectItem>
                    <SelectItem value="pay_later">Pay on arrival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className={label}>Personal note <span className="text-slate-600">(optional)</span></label>
              <textarea data-testid="qr-message" value={form.message} onChange={set("message")} rows={3}
                placeholder="e.g. Thanks for reaching out! This includes pickup, wait time and drop-off." className={`${input} resize-none`} />
            </div>
            <p className="text-xs text-slate-500">
              {form.payment_method === "online"
                ? "Card payments add 15% HST. The customer gets a pay-by-card link."
                : "No HST added. The customer gets the price and payment instructions."}
            </p>
            <button onClick={submit} disabled={loading} data-testid="qr-send-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3 font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send quote {quote.email ? "by email" : ""}
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="qr-result">
            <div className="rounded-sm bg-navy border border-gold/25 p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-slate-400">Quoted price</div>
              <div className="font-heading text-3xl font-bold text-gold mt-1">${result.amount.toFixed(2)}</div>
              {result.tax_amount > 0 && <div className="text-xs text-slate-500 mt-1">incl. HST ${result.tax_amount.toFixed(2)}</div>}
            </div>
            {result.email_sent ? (
              <div className="flex items-center justify-center gap-2 text-emerald-300 text-sm" data-testid="qr-email-sent">
                <MailCheck className="h-4 w-4" /> Emailed to {quote.email}
              </div>
            ) : (
              <p className="text-center text-xs text-amber-300">Email not sent (no customer email or sender not verified). Share the link below.</p>
            )}
            <div>
              <label className={label}>Secure pay / quote link</label>
              <div className="flex gap-2">
                <input readOnly value={payUrl} data-testid="qr-pay-url" className={`${input} text-gold`} />
                <button onClick={() => copy(payUrl)} className="shrink-0 rounded-sm border border-gold/40 p-3 text-gold hover:bg-gold/10">{copied === payUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a href={mailto} data-testid="qr-send-email" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><Mail className="h-4 w-4" /> Send by Email</a>
              <a href={sms} data-testid="qr-send-sms" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><MessageSquare className="h-4 w-4" /> Send by Text</a>
            </div>
            <a href={`/invoice/${result.id}`} target="_blank" rel="noopener noreferrer" data-testid="qr-invoice-url" className="block text-center text-xs text-slate-400 hover:text-gold">View / print invoice →</a>
            <button onClick={() => setOpen(false)} data-testid="qr-done" className="w-full rounded-full border border-gold/30 py-2.5 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200">Done</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
